import classNames from 'classnames/bind';
import { getCarbonEmissionInfo } from 'common/emissions';
import { IPromotion } from 'common/promotion';
import { getStopCount } from 'common/utils';
import { useCurationContext } from 'contexts/curation';
import { useReloadTimerContext } from 'contexts/reloadTimer';
import { SearchType, TripType } from 'contexts/searchParams';
import Route from 'list/common/route';
import { useState } from 'react';
import ItemHeader from '../item/header';
import ItemPriceList from '../item/priceList';
import getPromotions from '../promotion';
import {
  IFareTypeList,
  IInternationalContextResult,
  IScheduleDetail,
  ScheduleKeys,
} from '../types';
import { hasAdultNaverFare, toDays } from '../utils';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

interface IProps {
  fare: IFareTypeList;
  fareKeys: string[];
  flightResult: IInternationalContextResult;
  isTopPopularFlight?: boolean;
  journeyTime: string[];
  nClickKey: string;
  promotions: IPromotion[];
  sch: string;
  scheduleId: string;
  scheduleIndex: number;
  schedules: IScheduleDetail[][];
  selectedFareType?: string;
  tripType: TripType;
  showHelpTooltip?: boolean;
  selectFlight: (
    scheduleId: string,
    fareType: string,
    airline: string,
    labels: string[],
  ) => void;
}

const IndivisualItem = ({
  fare,
  fareKeys,
  flightResult,
  isTopPopularFlight = false,
  journeyTime,
  nClickKey,
  promotions,
  sch,
  scheduleId,
  scheduleIndex,
  schedules,
  selectedFareType,
  tripType,
  showHelpTooltip,
  selectFlight,
}: IProps) => {
  const { fareTypes = {}, airlines = {}, airports = {} } = flightResult;
  const { withReloadTimer } = useReloadTimerContext();
  const [foldFares, toggleFoldFares] = useState(true);
  const { getLabels, isCurationTarget } = useCurationContext();
  const schedule = schedules[scheduleIndex];
  const { sdt, sa, av } = schedule[0];
  const { edt, ea } = schedule[schedule.length - 1];
  const sdtDay = toDays(sdt);
  const edtDay = toDays(edt);
  const scheduleNumber = scheduleIndex + 1;

  const dayDiff = edtDay.startOf('d').diff(sdtDay.startOf('d'), 'd');
  const avs = Array.from(
    new Set(schedule.map(s => s[ScheduleKeys.airlineVendor])),
  );

  const oavs = Array.from(new Set(schedule.filter(s => s.oav).map(s => s.oav)));

  const selectSchedule = withReloadTimer(() => {
    if (fareKeys.length === 1) {
      // 요금이 하나뿐인 경우: 여정 선택 처리.
      selectFlight(scheduleId, fareKeys[0], av, labels);
    } else toggleFoldFares(!foldFares);
  });
  const stops = getStopCount(schedule);

  const minFare = fare[fareKeys[0]][0];
  const [promotion] = getPromotions(promotions, schedules, minFare);
  const labels = Array.from(getLabels(sch));

  const { carbonEmission, carbonEmissionAverage } = getCarbonEmissionInfo({
    schedule,
    carbonEmissionAverages: flightResult.carbonEmissionAverage,
  });

  return (
    <div
      className={cx('IndivisualItem', {
        with_labels: isCurationTarget(sch),
      })}
    >
      <div className={cx('inner')}>
        <div className={cx('schedule')}>
          <div className={cx('item')}>
            <ItemHeader
              airlines={airlines}
              avs={avs}
              carbonEmission={carbonEmission}
              carbonEmissionAverage={carbonEmissionAverage}
              fareType={fareTypes[fareKeys[0]]}
              isDirectPromotion={hasAdultNaverFare(minFare)}
              oavs={oavs}
              promotion={promotion}
              sch={sch}
            />
            <Route
              departureTime={sdtDay.format('HH:mm')}
              arrivalTime={edtDay.format('HH:mm')}
              departureCity={sa}
              arrivalCity={ea}
              dayDiff={dayDiff}
              stop={stops}
              searchType={SearchType.INTERNATIONAL}
              journeyTime={journeyTime}
              schedule={schedule}
              isList
              airlines={airlines}
              airports={airports}
              showHelpTooltip={showHelpTooltip}
            />
          </div>
        </div>
        <button
          className={cx('select_schedule')}
          type="button"
          data-event-nlog="click"
          data-event-area-code={`${nClickKey}.${isTopPopularFlight ? 'top' : ''}schedule${scheduleNumber}`}
          onClick={selectSchedule}
        />
        <hr />
        <ItemPriceList
          fare={fare}
          fareKeys={fareKeys}
          fareTypes={fareTypes}
          foldFares={foldFares}
          isTopPopularFlight={isTopPopularFlight}
          nClickKey={nClickKey}
          promotions={promotions}
          scheduleIndex={scheduleIndex}
          schedules={schedules}
          selectedFareType={selectedFareType}
          tripType={tripType}
          selectFlight={(fareType: string) =>
            selectFlight(scheduleId, fareType, av, labels)
          }
          toggleFoldFares={toggleFoldFares}
        />
      </div>
    </div>
  );
};

export default IndivisualItem;
